/**
 * The training screen's slice of the API (#190), in the shape of
 * `../api.ts`.
 *
 * The check is fetched apart from the module on purpose, and the answers
 * are never in either: `asked` is a paper with a seed on it, and the seed
 * goes back with the answers so the server can mark. Nothing in this file
 * knows what the right answer is, which is the point.
 */

import type {
  Asked,
  Marked,
  TrainingModule,
  TrainingProgress,
  TrainingRow,
} from "@baros/core";
import { request as shared } from "../api.js";

async function request<T>(
  token: string,
  method: "GET" | "POST" | "PUT" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  return shared<T>(token, method, path, body);
}

const at = (moduleId: string) => `/v1/training/${encodeURIComponent(moduleId)}`;

/** A module as a trainee is handed it: the pages, and no answers. */
export interface ModuleView {
  readonly module: TrainingModule;
  /** How many questions the check asks, since the questions themselves are withheld. */
  readonly questions: number;
  readonly progress: TrainingProgress;
}

/** The check, as handed out. `seed` goes back with the answers. */
export interface Paper {
  readonly seed: number;
  readonly asked: readonly Omit<Asked, "why">[];
  readonly passMark: number;
}

export interface MarkedCheck {
  readonly progress: TrainingProgress;
  readonly marked: Marked | null;
  /** Why, for what they got wrong. Only ever sent after marking. */
  readonly why?: Readonly<Record<string, string>>;
}

export const trainingApi = {
  course: (token: string) =>
    request<{ course: readonly TrainingRow[]; canEdit: boolean }>(token, "GET", "/v1/training"),

  module: (token: string, moduleId: string) => request<ModuleView>(token, "GET", at(moduleId)),

  check: (token: string, moduleId: string) => request<Paper>(token, "GET", `${at(moduleId)}/check`),

  /** How far they have read. Never backwards — the server keeps the furthest. */
  read: (token: string, moduleId: string, page: number) =>
    request<MarkedCheck>(token, "POST", `${at(moduleId)}/progress`, { kind: "page", page }),

  mark: (token: string, moduleId: string, seed: number, answers: Readonly<Record<string, readonly number[]>>) =>
    request<MarkedCheck>(token, "POST", `${at(moduleId)}/progress`, { kind: "check", seed, answers }),

  save: (token: string, module: Partial<TrainingModule> & { title: string }) =>
    module.moduleId
      ? request<{ module: TrainingModule }>(token, "PUT", at(module.moduleId), module)
      : request<{ module: TrainingModule }>(token, "POST", "/v1/training", module),

  remove: (token: string, moduleId: string) =>
    request<{ deleted: string }>(token, "DELETE", at(moduleId)),

  /** Who has done what, for a manager. */
  everyone: (token: string) =>
    request<{ progress: readonly TrainingProgress[] }>(token, "GET", "/v1/training/progress/all"),

  /** A presigned PUT for one picture, then the browser uploads to S3 itself. */
  presign: (token: string, contentType: string, sizeBytes: number) =>
    request<{ key: string; url: string }>(token, "POST", "/v1/training/images", { contentType, sizeBytes }),

  upload: async (url: string, file: File) => {
    const res = await fetch(url, { method: "PUT", headers: { "content-type": file.type }, body: file });
    if (!res.ok) throw new Error(`The picture would not upload (${res.status})`);
  },

  /** A short-lived link to show a picture. */
  imageUrl: (token: string, key: string) =>
    request<{ url: string }>(token, "GET", `/v1/training/images/url?key=${encodeURIComponent(key)}`),
};
